import { injectable } from 'tsyringe';
import { LoggerService } from '../../../shared/services/logger.service';
import { OWTrackRSWrapper } from '../../../shared/wrappers/ow-track-rs-event';
import { OWExtensionWrapper } from '../../ow-odk-wrappers/ow-extension-wrapper';
import { TrackEvent } from '../../../shared/models/tracking-event.model';
import { ContentRecommendationUserAction } from '../../../shared/constants';
import { OWUtilsWrapper } from '../../ow-odk-wrappers/ow-utils-wrapper';
import { TrackingExtra } from './models/tracking-extra.model';
import { RecommendationDatastoreService } from '../datastore-service/recommendation-datastore.service';
import { PersistentStorageService } from '../persistent-storage-service/persistent-storage.service';

// -----------------------------------------------------------------------------
@injectable()
export class PromotionAnalyticsTracker {
  // ---------------------------------------------------------------------------
  private readonly logger = LoggerService.getCategory(
    'PromotionAnalyticsTracker',
  );

  private phasePercent: number;
  // ---------------------------------------------------------------------------
  public constructor(
    private readonly OWtracking: OWTrackRSWrapper,
    private readonly owExtensionWrapper: OWExtensionWrapper,
    private readonly owUtilsWrapper: OWUtilsWrapper,
    private readonly datastoreService: RecommendationDatastoreService,
    private readonly persistentStorage: PersistentStorageService,
  ) {
    this.phasePercent = 0;
  }

  // ------------------------------------------------------------------------
  async sendPromotionAnalyticsError(gameId: number, promotionId: string) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'promotion_recommend_error';
    const extra: TrackingExtra = {};

    const overrides = {
      InGameId: gameId,
      Value: 1,
    };

    extra.app_version = await this.getAppVersion();
    extra.promotion_id = promotionId;

    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ---------------------------------------------------------------------------
  async sendPromotionRecommend(
    gameId: number,
    promotionId: string,
    userAction: ContentRecommendationUserAction,
  ) {
    if (typeof gameId === 'string') {
      gameId = parseInt(gameId);
    }

    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'promotion_recommend';

    const extra: TrackingExtra = {
      game_id: gameId,
      promotion_id: promotionId,
      app_version: await this.getAppVersion(),
      userAction,
    };

    const overrides = {};
    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ------------------------------------------------------------------------
  async sendPromotionOfferAnalyticsError(gameId: number, promotionId: string) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'promotion_offer_recommend_error';
    const extra: TrackingExtra = {};

    const overrides = {
      InGameId: gameId,
      Value: 1,
    };

    extra.app_version = await this.getAppVersion();
    extra.promotion_id = promotionId;

    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ------------------------------------------------------------------------
  async sendPromotionOfferRecommend(
    gameId: number,
    promotionId: string,
    userAction: ContentRecommendationUserAction,
  ) {
    if (typeof gameId === 'string') {
      gameId = parseInt(gameId);
    }

    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'promotion_offer_recommend';

    const extra: TrackingExtra = {
      game_id: gameId,
      promotion_id: promotionId,
      app_version: await this.getAppVersion(),
      userAction,
    };

    const overrides = {};
    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ------------------------------------------------------------------------
  async sendOfferAction(
    gameId: number,
    promotionId: string,
    userAction: ContentRecommendationUserAction,
  ) {
    if (typeof gameId === 'string') {
      gameId = parseInt(gameId);
    }

    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = 'promotion_offer_action';

    const extra: TrackingExtra = {
      game_id: gameId,
      promotion_id: promotionId,
      app_version: await this.getAppVersion(),
      userAction,
    };

    const overrides = {};
    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ------------------------------------------------------------------------
  async sendContentViewEvent(event: TrackEvent) {
    const shouldTrack = await this.shouldTrack();
    if (!shouldTrack) {
      return;
    }

    const name = event.name;
    const extra = event.extra as TrackingExtra;
    const overrides = event.overrides;
    if (!name || !extra) {
      return;
    }

    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ---------------------------------------------------------------------------
  async sendAppLaunched(gameId: number) {
    const name = 'promotions_app_launched';
    const appVersion = await this.getAppVersion();
    const extra: TrackingExtra = {
      game_id: gameId,
      app_version: appVersion,
    };

    this.sendAnalyticsEvent({ name, extra });
  }

  // ---------------------------------------------------------------------------
  sendPromotionSettingChanged(enable: boolean) {
    const name = 'promotion_setting_changed';
    const extra: TrackingExtra = {
      enable,
    };
    const overrides = {};

    this.sendAnalyticsEvent({ name, extra, overrides });
  }

  // ---------------------------------------------------------------------------
  private async getAppVersion() {
    await this.owExtensionWrapper.initialize();
    const { manifest } = this.owExtensionWrapper;
    return manifest.meta.version;
  }

  // ---------------------------------------------------------------------------
  private getPromotionViewCount(promotionId: string) {
    return this.persistentStorage.getPromotionAdUnitsById(promotionId) ?? 0;
  }

  // ---------------------------------------------------------------------------
  private sendAnalyticsEvent(data: TrackEvent) {
    this.OWtracking.trackRSEventV2(data.name, data.extra, data.overrides);
  }

  // ---------------------------------------------------------------------------
  private async shouldTrack() {
    return this.isPhasingInRange();
  }

  // ---------------------------------------------------------------------------
  private async isPhasingInRange() {
    if (this.phasePercent === 0) {
      this.phasePercent =
        await this.owUtilsWrapper.getPersistentPhasedPercent();
    }

    const configs = await this.datastoreService.getGlobalConfigurations();

    const configuredAnalyticsPhasing =
      configs?.passiveAnalyticsPersistentPhasing;

    const result = this.phasePercent <= configuredAnalyticsPhasing;
    return result;
  }
}
